import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import { Skeleton, Avatar, AvatarFallback, AvatarImage, Input, Button } from '@org/ui';
import { MessageSquare, Search, PenSquare, Circle, AlertCircle } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { useState, useMemo } from 'react';

export const Route = createFileRoute('/messages/')({
  component: MessagesIndexPage,
});

function MessagesIndexPage() {
  const { t } = useTranslation('community');
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, error, refetch } = communityTrpc.messages.listConversations.useQuery({
    limit: 50,
    offset: 0,
  });

  const conversations = (data?.conversations ?? []) as Array<{
    message_id: string;
    message: string;
    created_at: string;
    read_at: string | null;
    sender_id: string;
    partner_id: string;
    partner_first_name: string | null;
    partner_last_name: string | null;
    partner_display_name: string | null;
    partner_profile_image_url: string | null;
    unread_count: number;
  }>;

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const query = searchQuery.toLowerCase();
    return conversations.filter((conv) => {
      const partnerName = conv.partner_display_name ||
        [conv.partner_first_name, conv.partner_last_name].filter(Boolean).join(' ') ||
        '';
      return partnerName.toLowerCase().includes(query) ||
        conv.message.toLowerCase().includes(query);
    });
  }, [conversations, searchQuery]);

  const totalUnread = useMemo(() =>
    conversations.reduce((sum, conv) => sum + conv.unread_count, 0),
    [conversations]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{t('messages.title')}</h1>
          {totalUnread > 0 && (
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-2 text-xs font-medium text-white">
              {totalUnread}
            </span>
          )}
        </div>
        <Link to="/members">
          <Button size="sm" className="gap-2">
            <PenSquare className="h-4 w-4" aria-hidden="true" />
            {t('messages.newMessage')}
          </Button>
        </Link>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('messages.searchPlaceholder')}
          aria-label={t('messages.searchPlaceholder')}
          className="pl-10"
        />
      </div>

      <div className="divide-y rounded-2xl border border-border bg-card">
        {error ? (
          <div className="flex flex-col items-center gap-4 p-8 text-center">
            <AlertCircle className="h-12 w-12 text-destructive" aria-hidden="true" />
            <p className="text-muted-foreground">{t('common.error')}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        ) : isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
            </div>
          ))
        ) : filteredConversations.length === 0 ? (
          <div className="flex flex-col items-center gap-4 p-8 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
            <p className="text-muted-foreground">
              {searchQuery ? t('messages.noSearchResults') : t('messages.noConversations')}
            </p>
            {!searchQuery && (
              <Link to="/members">
                <Button variant="outline" size="sm">
                  {t('messages.findSomeone')}
                </Button>
              </Link>
            )}
          </div>
        ) : (
          filteredConversations.map((conv) => {
            const partnerName = conv.partner_display_name ||
              [conv.partner_first_name, conv.partner_last_name].filter(Boolean).join(' ') ||
              t('messages.unknownUser');
            const initial = partnerName.slice(0, 1).toUpperCase();
            const hasUnread = conv.unread_count > 0;

            return (
              <Link
                key={conv.partner_id}
                to="/messages/$partnerId"
                params={{ partnerId: conv.partner_id }}
                className={`flex items-center gap-3 p-4 transition-colors hover:bg-muted ${
                  hasUnread ? 'bg-primary/5' : ''
                }`}
              >
                <div className="relative">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={conv.partner_profile_image_url ?? undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {initial}
                    </AvatarFallback>
                  </Avatar>
                  {hasUnread && (
                    <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                      {conv.unread_count > 9 ? '9+' : conv.unread_count}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      {hasUnread && (
                        <Circle className="h-2 w-2 shrink-0 fill-primary text-primary" aria-hidden="true" />
                      )}
                      <p className={`truncate font-medium ${hasUnread ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {partnerName}
                      </p>
                    </div>
                    <span className="flex-shrink-0 text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(conv.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className={`truncate text-sm ${hasUnread ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    {conv.message}
                  </p>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </div>
  );
}
